import { useCallback, useEffect, useMemo, useState, type FC, type JSX } from "react";

import type { StateOrName } from "@uirouter/react";
import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes } from "@similarweb/styles";
import { ImageItemContainer, QueryBarWebsiteItem } from "@similarweb/ui-components";

import { FIRST_LAND_PREF_KEY } from "app/pages/activation/components/FirstLandModalProvider/constants";
import { PreferencesService } from "app/services/preferences/preferencesService";
import type { ISerializable } from "app/services/solutions2Services/HomepageDataFetchers/utils";
import { AutocompleteWebsitesMainItem } from "pro-features/components/AutocompleteWebsites/AutocompleteWebsitesMainItem";
import { queryBarItemTypes } from "app/components/React/HOCs/accessWithoutMandatoryParamsHOC";
import type { ISite } from "pro-features/components/Workspace/Wizard/src/types";
import { AutocompleteWithLineOfBusiness } from "pro-features/components/AutocompleteWebsites/AutocompleteWithLineOfBusiness";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import {
    getRecentsAnalysis,
    getRecentsByParentState,
} from "app/services/solutions2Services/HomepageDataFetchers/NewModulesHomepageDataFetcher";
import type { FirstLandingState } from "single-spa/react-app/router/utils/paramsRouting/types";

import { QueryBarItem } from "../QueryBarItem";
import { autocompleteStates } from "../WebsiteQueryBarTypes";

import { EmptyRecent } from "./EmptyRecent";
import { getHasEmptyState } from "./helpers";
import type { LobData, LobItem } from "./useLobSites";
import { LoadingComponent, PageOverlay } from "./WebsiteQueryBar";
import { allTrackers } from "app/services/track/track";

interface ISelectedItem {
    compareItems?: Array<{ domain: string }>;
    name?: string;
    mainSite?: {
        domain: string;
    };
}
export const QueryBarItemContainer = styled.div<{ className?: string }>`
    margin-left: 16px;
`;
const StyledSWReactIcons = styled(SWReactIcons)<{ className?: string }>`
    svg {
        path {
            fill: ${colorsPalettes.blue[400]};
        }
    }
`;

const queryItemImageRenderer = (): JSX.Element => {
    return (
        <ImageItemContainer borderColor={colorsPalettes.blue[400]}>
            <StyledSWReactIcons
                iconName="globe"
                size="xs"
            />
        </ImageItemContainer>
    );
};
interface IEmptyWebsiteQueryBarProps {
    filterListItems?: (items: ISite[]) => ISite[];
    filterRecentsBy?: StateOrName | StateOrName[];
    lobData?: LobData;
    suggestions?: ISite[];
    hideSidebarOverlay?: boolean;
    recentsDedupeFunction?: (recent: ISerializable) => string;
    queryBarSearchHeader?: React.ReactNode;
}

export const EmptyWebsiteQueryBar: FC<IEmptyWebsiteQueryBarProps> = ({
    filterListItems = null,
    filterRecentsBy,
    lobData = { showLob: false, lobSites: [], isLobLoading: false, topAnalyzedLOBWebsites: [] },
    suggestions,
    hideSidebarOverlay = true,
    recentsDedupeFunction,
    queryBarSearchHeader,
}) => {
    const { showLob, lobSites, isLobLoading, topAnalyzedLOBWebsites: topLobWebsites } = lobData;

    const i18n = useTranslation();

    const { state } = useCurrentStateAndParams();

    const [recent, setRecent] = useState([]);
    const [isFetching, setIsFetching] = useState(true);
    const [isOverlayOpen, setIsOverlayOpen] = useState(true);
    const isFirstLand = PreferencesService.get(FIRST_LAND_PREF_KEY) as string | boolean | undefined;

    const hasEmptyState = getHasEmptyState(state?.name as FirstLandingState, Boolean(isFirstLand));

    const fetchRecent = useCallback(async () => {
        try {
            if (filterRecentsBy) {
                return getRecentsByParentState(filterRecentsBy, true, recentsDedupeFunction);
            }

            return await getRecentsAnalysis("website", true, recentsDedupeFunction);
        } catch (error) {
            Sentry.error("Error fetching recents - WebsiteQueryBar -", error);
            return [autocompleteStates.ERROR];
        }
    }, [filterRecentsBy, recentsDedupeFunction]);

    useEffect(() => {
        let isMounted = true;

        const getRecentItems = async (): Promise<void> => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const recentItems = await fetchRecent();
            if (!isMounted) {
                return;
            }

            setRecent(recentItems);
            setIsFetching(false);
        };

        void getRecentItems();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        setIsOverlayOpen(true);
    }, [state.name]);

    const onItemSelected = useCallback((item: ISelectedItem, type: string): void => {
        const comparedSites = item.compareItems?.map((site) => site.domain);
        const keys = !item.compareItems ? item.name : [item.mainSite?.domain, comparedSites].join(",");

        allTrackers.dispatchTrackEvent("Search Bar", "click", `${type || ""}/Empty State/${keys || ""}`);

        proNavigator.updateQueryParams(
            { key: keys },
            {
                location: "replace",
                reload: false,
            },
        );
    }, []);

    const onLobItemSelected = useCallback((item: LobItem): void => {
        const keys = item.name;
        allTrackers.dispatchTrackEvent("Search Bar", "click", `lob/Empty State/${keys || ""}`);

        proNavigator.updateQueryParams(
            { key: keys },
            {
                location: "replace",
                reload: false,
            },
        );
    }, []);

    const onOverlayClick = useCallback((): void => {
        setIsOverlayOpen(false);
    }, []);

    const { recentSearches, recentTitle } = useMemo(() => {
        const hasRealRecent =
            Array.isArray(recent) &&
            recent.length > 0 &&
            recent[0] !== autocompleteStates.NO_DATA_RECENTS &&
            recent[0] !== autocompleteStates.ERROR;

        if (hasRealRecent) {
            return { recentSearches: recent, recentTitle: "" };
        }

        const hasSuggestions = Array.isArray(suggestions) && suggestions.length > 0;

        if (hasSuggestions) {
            return {
                recentSearches: suggestions,
                recentTitle: "autocomplete.websitesMain.seperator.text.suggestions",
            };
        }

        return { recentSearches: [], recentTitle: "" };
    }, [recent, suggestions]);

    return (
        <>
            <QueryBarItemContainer>
                <QueryBarItem
                    id="FakeWebsiteQueryBar"
                    activeItem={isOverlayOpen ? "FakeWebsiteQueryBar" : ""}
                    isLoading={isFetching}
                    renderComponent={
                        <QueryBarWebsiteItem
                            text={i18n("querybar.missing.param.state.query.placeholder", {
                                itemType: i18n(queryBarItemTypes.website),
                            })}
                            isCompare={false}
                            onItemClick={() => setIsOverlayOpen((prev) => !prev)}
                            renderImageComponent={queryItemImageRenderer}
                            image=""
                        />
                    }
                    searchComponent={
                        !showLob ? (
                            <AutocompleteWebsitesMainItem
                                className="AutocompleteWebsitesQueryBar"
                                onClick={onItemSelected}
                                onClose={onOverlayClick}
                                recentSearches={recentSearches}
                                recentsTitle={recentTitle}
                                showCompareSites={!proNavigator.current().hideCompareButton}
                                autocompleteProps={{ width: 430 }}
                                filterListItems={filterListItems}
                                emptyState={hasEmptyState ? <EmptyRecent /> : undefined}
                            >
                                {queryBarSearchHeader}
                            </AutocompleteWebsitesMainItem>
                        ) : (
                            <AutocompleteWithLineOfBusiness
                                className="AutocompleteWebsitesQueryBar"
                                onClose={onOverlayClick}
                                autocompleteProps={{ width: 430 }}
                                lobSites={lobSites}
                                topAnalyzedLOBWebsites={topLobWebsites}
                                isLoading={isLobLoading}
                                onClick={onLobItemSelected}
                            />
                        )
                    }
                    loadingComponent={
                        <LoadingComponent headerText={i18n("autocomplete.websitesMain.seperator.text.recents")} />
                    }
                />
            </QueryBarItemContainer>
            <div onClick={onOverlayClick}>
                <PageOverlay
                    isOpen={isOverlayOpen}
                    hideSidebarOverlay={false}
                />
            </div>
        </>
    );
};
